//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import pluralize from "pluralize";
// autopilot-suggestion-card imports
import { IState, IProps, IContainer } from "./AutopilotSuggestionsCard.d";
import * as s from "./AutopilotSuggestionsCard.styles";
import { AutopilotSuggestionsNav } from "./autopilot-suggestions-nav";
import { AutopilotSuggestionInvite } from "./autopilot-suggestion-invite";
import { AutopilotSuggestionEnableMeeting } from "./autopilot-suggestion-enable-meeting";
import { AutopilotSuggestionLessenConstraint } from "./autopilot-suggestion-lessen-constraint";
// other components
import { H2 } from "@ui";
import { asCard } from "@components/card";
import { CardTopLine } from "@components/card-top-line";
// util
import { track, TrackingEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";
// state
import { markDefragValueKnobsAsDismissed } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { IReduxState } from "@state/reducers/root.reducer";
import { lastAutopilotSuggestionsCardTimestamp } from "@state/local-storage";
// material-ui
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class AutopilotSuggestionsCardComponent extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      suggestionIndex: 0,
      exploding: false,
      presentedKnobUrns: [],
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onMarkAsDismissedFailure = () => {
    logger.error("Failed to mark autopilot suggestions as dismissed");
  };

  private markAsDismissed = () => {
    const { tailoredDefragCardSummary } = this.props.org;
    if (!tailoredDefragCardSummary || tailoredDefragCardSummary.__typename === "GraphEntityError") {
      return;
    }

    const suggestions = [
      ...tailoredDefragCardSummary.knobs.inviteOrganizerKnobs,
      ...tailoredDefragCardSummary.knobs.autopilotMeetingKnobs,
      ...tailoredDefragCardSummary.knobs.lessenAutopilotConstraintKnobs,
    ];
    const knobUrns = suggestions.map((s) => s.knobUrn);

    markDefragValueKnobsAsDismissed(
      this.props.relay.environment,
      {
        knobUrns,
        orgRelayId: this.props.org.id,
      },
      () => null,
      this.onMarkAsDismissedFailure,
    );
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  private removeCard = () => {
    // mark suggestions as seen
    this.markAsDismissed();

    // store our last timestamp
    lastAutopilotSuggestionsCardTimestamp.set(new Date().valueOf().toString());

    // track
    track(TrackingEvents.AUTOPILOT_SUGGESTIONS.DISMISS_AUTOPILOT_SUGGESTIONS_CARD);

    if (this.props.removeCard) {
      this.props.removeCard();
    }
  };

  private onSuggestionIndexChange = (suggestionIndex: number) => {
    this.setState({ suggestionIndex });
  };

  private onMarkAsPresentedCallback = (knobUrn: ISchema.IUrn) => {
    this.setState({ presentedKnobUrns: [...this.state.presentedKnobUrns, knobUrn] });
  };

  private onAction = (tailoredDefragCardSummary: ISchema.ITailoredDefragCardSummary) => {
    // green animation
    this.setState({ exploding: true });

    // then if not last suggestion, next
    const suggestions = [
      ...tailoredDefragCardSummary.knobs.inviteOrganizerKnobs,
      ...tailoredDefragCardSummary.knobs.autopilotMeetingKnobs,
      ...tailoredDefragCardSummary.knobs.lessenAutopilotConstraintKnobs,
    ];

    if (this.state.suggestionIndex === suggestions.length - 1) {
      return;
    }

    this.onSuggestionIndexChange(this.state.suggestionIndex + 1);
  };

  private onAnimationEnd = (_tailoredDefragCardSummary: ISchema.ITailoredDefragCardSummary) => {
    this.setState({ exploding: false });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderSuggestion = (
    suggestion:
      | ISchema.IInviteOrganizerKnob
      | ISchema.IAutopilotMeetingKnob
      | ISchema.ILessenAutopilotConstraintKnob,
    index: number,
    tailoredDefragCardSummary: ISchema.ITailoredDefragCardSummary,
  ) => {
    const { org } = this.props;

    switch (suggestion.__typename) {
      case "InviteOrganizerKnob":
        return (
          <AutopilotSuggestionInvite
            key={`autopilot-suggestion-${index}`}
            org={org}
            referencedEvents={tailoredDefragCardSummary.referencedEvents}
            inviteOrganizerKnob={suggestion}
            onAction={() => this.onAction(tailoredDefragCardSummary)}
            active={this.state.suggestionIndex === index}
            onMarkAsPresentedCallback={this.onMarkAsPresentedCallback}
          />
        );
      case "AutopilotMeetingKnob":
        return (
          <AutopilotSuggestionEnableMeeting
            key={`autopilot-suggestion-${index}`}
            org={org}
            calendarId={tailoredDefragCardSummary.knobs.calendarId}
            referencedEvents={tailoredDefragCardSummary.referencedEvents}
            autopilotMeetingKnob={suggestion}
            onAction={() => this.onAction(tailoredDefragCardSummary)}
            active={this.state.suggestionIndex === index}
            onMarkAsPresentedCallback={this.onMarkAsPresentedCallback}
          />
        );
      case "LessenAutopilotConstraintKnob":
        return (
          <AutopilotSuggestionLessenConstraint
            key={`autopilot-suggestion-${index}`}
            org={org}
            calendarId={tailoredDefragCardSummary.knobs.calendarId}
            referencedEvents={tailoredDefragCardSummary.referencedEvents}
            lessenAutopilotConstraintKnob={suggestion}
            onAction={() => this.onAction(tailoredDefragCardSummary)}
            active={this.state.suggestionIndex === index}
            onMarkAsPresentedCallback={this.onMarkAsPresentedCallback}
          />
        );
      default:
        return <span key={`autopilot-suggestion-${index}`} />;
    }
  };

  public render() {
    const { tailoredDefragCardSummary } = this.props.org;
    if (!tailoredDefragCardSummary || tailoredDefragCardSummary.__typename === "GraphEntityError") {
      return null;
    }

    const suggestionsCount =
      tailoredDefragCardSummary.knobs.inviteOrganizerKnobs.length +
      tailoredDefragCardSummary.knobs.autopilotMeetingKnobs.length +
      tailoredDefragCardSummary.knobs.lessenAutopilotConstraintKnobs.length;

    return (
      <div className={this.props.classes.container}>
        <div className={this.props.classes.topLine}>
          <CardTopLine
            timeStamp={tailoredDefragCardSummary.defragTime}
            clear={this.removeCard}
            label="Assistant"
          />
        </div>
        <div className={this.props.classes.headerBody}>
          <H2 className={this.props.classes.header}>
            Clockwise identified {suggestionsCount} {pluralize("way", suggestionsCount)} for you to
            improve your schedule
          </H2>
        </div>
        {suggestionsCount > 1 ? (
          <AutopilotSuggestionsNav
            currentIndex={this.state.suggestionIndex}
            suggestionsCount={suggestionsCount}
            onSuggestionIndexChange={this.onSuggestionIndexChange}
          />
        ) : (
          <span />
        )}
        <div className={this.props.classes.suggestionsContainer}>
          <div
            className={`${this.state.exploding ? this.props.classes.explosion : ""}`}
            onAnimationEnd={() => this.onAnimationEnd(tailoredDefragCardSummary)}
          ></div>
          <div
            className={this.props.classes.suggestions}
            style={{ ...s.getSuggestionOffsetStyles(this.state.suggestionIndex) }}
          >
            {tailoredDefragCardSummary.knobs.inviteOrganizerKnobs.length
              ? tailoredDefragCardSummary.knobs.inviteOrganizerKnobs.map((s, i) =>
                  this.renderSuggestion(s, i, tailoredDefragCardSummary),
                )
              : null}
            {tailoredDefragCardSummary.knobs.autopilotMeetingKnobs.length
              ? tailoredDefragCardSummary.knobs.autopilotMeetingKnobs.map((s, i) =>
                  this.renderSuggestion(s, i, tailoredDefragCardSummary),
                )
              : null}
            {tailoredDefragCardSummary.knobs.lessenAutopilotConstraintKnobs.length
              ? tailoredDefragCardSummary.knobs.lessenAutopilotConstraintKnobs.map((s, i) =>
                  this.renderSuggestion(s, i, tailoredDefragCardSummary),
                )
              : null}
          </div>
        </div>
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const AutopilotSuggestionsCardStyled = withStyles(s.styles)(
  AutopilotSuggestionsCardComponent,
);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(AutopilotSuggestionsCardStyled);

//////////////////
// RELAY
//////////////////
import { fragments } from "./AutopilotSuggestionsCard.gql";
export const AutopilotSuggestionsCard = createFragmentContainer<IContainer>(
  asCard<IContainer>(reduxContainer),
  fragments,
);
